import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    country: "",
    enquirtType: "",
    message: "",
    agree: false,
  });

  const inquiryOptions = [
    { value: "web-development", label: "Web Development" },
    { value: "database-migration", label: "Database Migration" },
    { value: "bl-analytics", label: "BL Analytics" },
    { value: "cloud-management", label: "Cloud Management" },
    { value: "cloud-migration", label: "Cloud Migration" },
    { value: "cloud-consultation", label: "Cloud Consultation" },
    { value: "it-support", label: "IT Support" },
  ];

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Validation
  const validateForm = () => {
    let newErrors = {};

    if (!formData.fullName || formData.fullName.trim().length < 3) {
      newErrors.fullName = "Full name must be at least 3 characters.";
    }

    if (!formData.email) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format.";
    }

    if (!formData.phone.trim()) newErrors.phone = "Phone required";
    else if (!/^\d{10}$/.test(formData.phone))
      newErrors.phone = "Phone must be exactly 10 digits";

    if (!formData.country) {
      newErrors.country = "Country is required.";
    }

    if (!formData.enquirtType) {
      newErrors.enquirtType = "Please select an inquiry type.";
    }

    if (!formData.message || formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters.";
    }

    if (!formData.agree) {
      newErrors.agree = "You must agree to the Privacy Policy.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change & clear errors
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const handleDropdownChange = (value) => {
    setFormData((prev) => ({ ...prev, enquirtType: value }));
    setErrors((prev) => ({ ...prev, enquirtType: undefined }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      customerName: formData.fullName,
      mobNumber: formData.phone,
      email: formData.email,
      orgName: formData.company,
      details: formData.message,
      enquirtType: formData.enquirtType,
    };

    try {
      const res = await axios.post(
        "https://vikrantpatiloffice.in:8443/websitecontactus/saveContactDetails",
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      // If you reach here, request was successful
      Swal.fire({
        icon: "success",
        title: "Message Sent 🎉",
        text: "Our team will contact you shortly.",
        position: "center",
        showConfirmButton: false,
        timer: 5000,
        backdrop: `rgba(0,0,0,0.4) left top no-repeat`,
      });

      setSubmitted(true);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        company: "",
        country: "",
        enquirtType: "",
        message: "",
        agree: false,
      });
    } catch (err) {
      // Axios automatically throws for HTTP errors
      Swal.fire({
        icon: "error",
        title: "Oops... 😢",
        text: err.response?.data || err.message || "Something went wrong!",
        position: "top",
        showConfirmButton: true,
        confirmButtonText: "OK",
        confirmButtonColor: "#0D3559",
        background: "#fff",
        color: "#111",
        buttonsStyling: true,
      });
    }
  };

  const formVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const buttonVariants = {
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#0D3559] to-indigo-800 h-64 flex items-center justify-center relative overflow-hidden mb-28">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-white text-4xl md:text-5xl font-bold text-center px-4"
        >
          Contact Us
        </motion.h1>
      </div>

      {/* Form + Info */}
      <div className="max-w-6xl mx-auto -mt-20 bg-white shadow-2xl rounded-2xl p-8 grid md:grid-cols-2 gap-12">
        {/* Contact Form */}
        <motion.form
          onSubmit={handleSubmit}
          className="space-y-6"
          initial="hidden"
          animate="visible"
          variants={formVariants}
          autoComplete="off"
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-800">
            Get in Touch
          </h2>

          {/* Full Name + Email */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Full Name"
                className={`w-full border p-4 rounded-xl shadow-sm focus:ring-2 focus:ring-[#0D3559] focus:outline-none transition ${errors.fullName ? "border-red-500" : "border-gray-300"
                  }`}
              />
              {errors.fullName && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.fullName}
                </span>
              )}
            </div>

            <div className="flex flex-col">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                className={`w-full border p-4 rounded-xl shadow-sm focus:ring-2 focus:ring-[#0D3559] focus:outline-none transition ${errors.email ? "border-red-500" : "border-gray-300"
                  }`}
              />
              {errors.email && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.email}
                </span>
              )}
            </div>
          </div>

          {/* Phone + Company */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <input
                type="text" // use text to control validation yourself
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ""); // allow digits only
                  if (value.length <= 10) {
                    handleChange({ target: { name: "phone", value } });
                  }
                }}
                placeholder="Phone"
                className={`w-full border p-4 rounded-xl shadow-sm focus:ring-2 focus:ring-[#0D3559] focus:outline-none transition ${errors.phone ? "border-red-500" : "border-gray-300"
                  }`}
              />
              {errors.phone && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.phone}
                </span>
              )}
            </div>

            <div className="flex flex-col">
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Company"
                className="w-full border p-4 rounded-xl shadow-sm focus:ring-2 focus:ring-[#0D3559] focus:outline-none transition border-gray-300"
              />
            </div>
          </div>

          {/* Country + Inquiry */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="Country"
                className={`w-full border p-4 rounded-xl shadow-sm focus:ring-2 focus:ring-[#0D3559] focus:outline-none transition ${errors.country ? "border-red-500" : "border-gray-300"
                  }`}
              />
              {errors.country && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.country}
                </span>
              )}
            </div>

            <div className="flex flex-col">
              <Dropdown
                options={inquiryOptions}
                value={formData.enquirtType}
                onChange={handleDropdownChange}
                placeholder="Select Inquiry Type"
              />
              {errors.enquirtType && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.enquirtType}
                </span>
              )}
            </div>
          </div>

          {/* Message */}
          <div className="flex flex-col">
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Your Message"
              rows="5"
              className={`w-full border p-4 rounded-xl shadow-sm focus:ring-2 focus:ring-[#0D3559] focus:outline-none transition ${errors.message ? "border-red-500" : "border-gray-300"
                }`}
            ></textarea>
            {errors.message && (
              <span className="text-red-500 text-xs mt-1">
                {errors.message}
              </span>
            )}
          </div>

          {/* Checkbox */}
          <div className="flex flex-col">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="agree"
                checked={formData.agree}
                onChange={handleChange}
                className="w-5 h-5 accent-[#0D3559]"
              />
              <span className="text-gray-700 text-sm">
                I agree to the Privacy Policy & Terms
              </span>
            </label>
            {errors.agree && (
              <span className="text-red-500 text-xs mt-1">{errors.agree}</span>
            )}
          </div>

          {/* Submit */}
          <motion.button
            type="submit"
            className="bg-[#0D3559] text-white px-8 py-3 rounded-xl shadow-lg font-semibold w-full md:w-auto transition-colors duration-300"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Send Message
          </motion.button>

          {/* {submitted && (
            <p className="text-green-600 mt-2 font-medium animate-pulse">
              Message sent successfully!
            </p>
          )} */}
        </motion.form>

        {/* Contact Info + Map */}
        <motion.div
          className="space-y-6"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-800">Our Office</h2>

          <div className="p-6 bg-gradient-to-tr from-gray-100 to-white rounded-2xl shadow-lg space-y-2 border border-gray-200">
            <p className="font-semibold text-gray-900">Flint Infotech</p>
            <p>Office No : S-17, 2nd Floor</p>
            <p>Haware Fantasia Business Park</p>
            <p>Vashi, Navi Mumbai-400703, India</p>
            <p>Email : info@flintinfotech.com | sales@flintinfotech.com</p>
            <p>GST No : 27AAJFF4779L1ZP</p>
          </div>

          <div className="h-72 md:h-96 w-full rounded-2xl overflow-hidden shadow-xl border-2 border-gray-200">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1928535.2699141786!2d72.46382203441276!3d19.253222935884565!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x85ceb00ef544cb81%3A0x9c01ca9f5af0c5f3!2sFlint%20Infotech!5e0!3m2!1sen!2sin!4v1758529783910!5m2!1sen!2sin"
              className="w-full h-full border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Flint Infotech Location"
            ></iframe>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;

// Dropdown component (unchanged)
const Dropdown = ({ options = [], value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option.value);
    setOpen(false);
  };

  return (
    <div className="relative w-full" ref={ref}>
      <div
        onClick={() => setOpen(!open)}
        className="w-full border border-gray-300 p-4 rounded-xl shadow-sm bg-white flex justify-between items-center cursor-pointer hover:ring-2 hover:ring-[#0D3559] transition"
      >
        <span className="text-gray-700">
          {value ? options.find((o) => o.value === value)?.label : placeholder}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${open ? "rotate-180" : ""
            }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg overflow-hidden"
          >
            {options.map((option) => (
              <motion.div
                key={option.value}
                onClick={() => handleSelect(option)}
                whileHover={{ backgroundColor: "#0D3559", color: "white" }}
                className="px-4 py-3 cursor-pointer text-black transition"
              >
                {option.label}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
